import { AnimatePresence, motion } from "motion/react"
import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { DialogBox } from "@/components/DialogBox"
import { useTypewriter } from "@/hooks/useTypewriter"
import { i18n } from "@/i18n"
import { useGuideStore, useMapStore, useTransitionStore } from "@/store"
import type { Dialogue, GuideHighlightTarget } from "@/types"
import { getFontSize } from "@/utils/adaptFont"

import styles from "./index.module.scss"

const GUIDE_HIGHLIGHT_DEFAULT_PADDING = 8
const GUIDE_HIGHLIGHT_BASE_STROKE_WIDTH = 6
const GUIDE_HIGHLIGHT_STROKE_COLOR = "#ecaa61"

interface HighlightRect {
    x: number
    y: number
    width: number
    height: number
}

const getScaleFromTransform = (transform: string): { x: number; y: number } => {
    if (!transform || transform === "none") {
        return { x: 1, y: 1 }
    }

    try {
        const matrix = new DOMMatrixReadOnly(transform)
        return {
            x: Math.max(Math.hypot(matrix.a, matrix.b), 0.0001),
            y: Math.max(Math.hypot(matrix.c, matrix.d), 0.0001),
        }
    } catch {
        return { x: 1, y: 1 }
    }
}

const viewportToLocal = (vx: number, vy: number): { x: number; y: number } => {
    if (document.body.dataset.rotate !== "1") return { x: vx, y: vy }
    return { x: vy, y: window.innerWidth - vx }
}

const buildHighlightRects = (
    targets: GuideHighlightTarget[],
): HighlightRect[] => {
    const scale = getFontSize() / 16
    const isRotated = document.body.dataset.rotate === "1"

    return targets.flatMap((target) => {
        const el = document.querySelector(target.selector)
        if (!(el instanceof Element)) return []

        const rect = el.getBoundingClientRect()
        const computedStyle = window.getComputedStyle(el)
        const lockScale = target.lockScale ?? true
        const targetScale = lockScale
            ? getScaleFromTransform(computedStyle.transform)
            : { x: 1, y: 1 }
        const padding =
            (target.padding ?? GUIDE_HIGHLIGHT_DEFAULT_PADDING) * scale

        if (isRotated) {
            const p1 = viewportToLocal(rect.left, rect.top)
            const p2 = viewportToLocal(rect.right, rect.top)
            const p3 = viewportToLocal(rect.left, rect.bottom)
            const p4 = viewportToLocal(rect.right, rect.bottom)

            const minX = Math.min(p1.x, p2.x, p3.x, p4.x)
            const minY = Math.min(p1.y, p2.y, p3.y, p4.y)
            const maxX = Math.max(p1.x, p2.x, p3.x, p4.x)
            const maxY = Math.max(p1.y, p2.y, p3.y, p4.y)

            const baseWidth = (maxX - minX) / targetScale.x
            const baseHeight = (maxY - minY) / targetScale.y
            const cx = (minX + maxX) / 2
            const cy = (minY + maxY) / 2
            const width = Math.max(1, baseWidth + padding * 2)
            const height = Math.max(1, baseHeight + padding * 2)

            return [{ x: cx - width / 2, y: cy - height / 2, width, height }]
        }

        const baseWidth = rect.width / targetScale.x
        const baseHeight = rect.height / targetScale.y
        const centerX = rect.x + rect.width / 2
        const centerY = rect.y + rect.height / 2
        const width = Math.max(1, baseWidth + padding * 2)
        const height = Math.max(1, baseHeight + padding * 2)

        return [
            {
                x: centerX - width / 2,
                y: centerY - height / 2,
                width,
                height,
            },
        ]
    })
}

function DialoguePhaseView({
    dialogues,
    index,
    onAdvance,
}: {
    dialogues: Dialogue[]
    index: number
    onAdvance: () => void
}) {
    const skipGuide = useGuideStore((s) => s.skipGuide)

    const dialogue = dialogues[index]
    const dialogueText = dialogue ? i18n.get(dialogue.text as never) : ""

    const { displayed, isDone, skip } = useTypewriter(dialogueText, 30)

    const handleClick = useCallback(() => {
        if (!isDone) {
            skip()
            return
        }
        onAdvance()
    }, [isDone, skip, onAdvance])

    const curSide: "left" | "right" = dialogue?.side ?? "left"

    let leftSlot: { character: string; expression?: string } | undefined
    let rightSlot: { character: string; expression?: string } | undefined

    if (dialogue) {
        if (curSide === "left") {
            leftSlot = {
                character: dialogue.speaker,
                expression: dialogue.expression,
            }
            for (let i = index - 1; i >= 0; i--) {
                if ((dialogues[i].side ?? "left") === "right") {
                    rightSlot = {
                        character: dialogues[i].speaker,
                        expression: dialogues[i].expression,
                    }
                    break
                }
            }
        } else {
            rightSlot = {
                character: dialogue.speaker,
                expression: dialogue.expression,
            }
            for (let i = index - 1; i >= 0; i--) {
                if ((dialogues[i].side ?? "left") === "left") {
                    leftSlot = {
                        character: dialogues[i].speaker,
                        expression: dialogues[i].expression,
                    }
                    break
                }
            }
        }
    }

    return (
        <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={handleClick}
        >
            <div className={styles.prologueBg} />

            <motion.div
                className={styles.prologueDialogue}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
            >
                <DialogBox
                    leftCharacter={leftSlot?.character}
                    leftExpression={leftSlot?.expression}
                    rightCharacter={rightSlot?.character}
                    rightExpression={rightSlot?.expression}
                    active={curSide}
                    text={displayed}
                    fullText={dialogueText}
                />
            </motion.div>

            <button
                className={styles.skipBtn}
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    skipGuide()
                }}
                style={{ pointerEvents: "auto" }}
            >
                {i18n.get("map.guide.skip")}
            </button>
        </motion.div>
    )
}

function TutorialView() {
    const {
        currentStep,
        steps,
        dialogueIndex,
        advanceStepDialogue,
        nextStep,
        skipGuide,
        startTransition,
    } = useGuideStore()

    const moveToNode = useMapStore((s) => s.moveToNode)
    const transitionActive = useTransitionStore((s) => s.isActive)

    const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([])
    const hasExecutedEnter = useRef<number>(-1)

    const step = steps[currentStep]

    useEffect(() => {
        if (!step) return
        if (step.onEnter?.moveToNode && hasExecutedEnter.current !== step.id) {
            hasExecutedEnter.current = step.id
            moveToNode(step.onEnter.moveToNode)
        }
    }, [step, moveToNode])

    useEffect(() => {
        if (!step?.highlightTargets?.length) {
            setHighlightRects([])
            return
        }

        let frameId = 0
        const observedElements = step.highlightTargets
            .map((target) => document.querySelector(target.selector))
            .filter((el): el is Element => el instanceof Element)

        const updateRects = () => {
            setHighlightRects(buildHighlightRects(step.highlightTargets ?? []))
        }

        const scheduleUpdate = () => {
            cancelAnimationFrame(frameId)
            frameId = window.requestAnimationFrame(updateRects)
        }

        scheduleUpdate()
        const timer = window.setTimeout(scheduleUpdate, 700)
        window.addEventListener("resize", scheduleUpdate)

        const resizeObserver =
            typeof ResizeObserver === "undefined"
                ? null
                : new ResizeObserver(scheduleUpdate)

        for (const el of observedElements) {
            resizeObserver?.observe(el)
        }

        return () => {
            cancelAnimationFrame(frameId)
            window.clearTimeout(timer)
            window.removeEventListener("resize", scheduleUpdate)
            resizeObserver?.disconnect()
        }
    }, [step])

    useEffect(() => {
        if (!step?.disabledTargets?.length) return

        const disabledEls = step.disabledTargets
            .map((selector) => document.querySelector(selector))
            .filter((el): el is HTMLElement => el instanceof HTMLElement)

        for (const el of disabledEls) {
            el.style.pointerEvents = "none"
            el.style.opacity = "0.4"
        }

        return () => {
            for (const el of disabledEls) {
                el.style.pointerEvents = ""
                el.style.opacity = ""
            }
        }
    }, [step])

    const dialogue: Dialogue | null = step?.dialogues?.[dialogueIndex] ?? null

    const dialogueText =
        !transitionActive && dialogue ? i18n.get(dialogue.text as never) : ""

    const { displayed, isDone, skip } = useTypewriter(dialogueText, 30)

    const handleOverlayClick = useCallback(() => {
        if (!step) return

        if (!isDone) {
            skip()
            return
        }

        if (step.dialogues.length > 0) {
            const isLastDialogue = advanceStepDialogue()
            if (!isLastDialogue) return
        }

        if (step.onComplete?.withTransition && step.onComplete.switchRegion) {
            startTransition(step.onComplete.switchRegion)
            return
        }

        nextStep()
    }, [step, isDone, skip, advanceStepDialogue, nextStep, startTransition])

    const pointerEvents = useMemo(() => {
        if (!step) return "auto" as const
        return step.requireTargetClick ? ("none" as const) : ("auto" as const)
    }, [step])

    if (!step) return null

    const side: "left" | "right" = dialogue?.side ?? "left"
    const highlightStrokeWidth = Math.max(
        1,
        (GUIDE_HIGHLIGHT_BASE_STROKE_WIDTH * getFontSize()) / 16,
    )

    return (
        <div
            className={styles.overlay}
            onClick={handleOverlayClick}
            style={{ pointerEvents }}
        >
            <svg className={styles.svgMask}>
                <defs>
                    <mask id="guide-mask">
                        <rect width="100%" height="100%" fill="white" />
                        {highlightRects.map((rect) => (
                            <rect
                                key={`${rect.x}-${rect.y}-${rect.width}-${rect.height}`}
                                x={rect.x}
                                y={rect.y}
                                width={rect.width}
                                height={rect.height}
                                rx="0"
                                fill="black"
                            />
                        ))}
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0,0,0,0.6)"
                    mask="url(#guide-mask)"
                />
                {highlightRects.map((rect) => (
                    <rect
                        key={`stroke-${rect.x}-${rect.y}-${rect.width}-${rect.height}`}
                        x={rect.x}
                        y={rect.y}
                        width={rect.width}
                        height={rect.height}
                        rx="0"
                        fill="none"
                        stroke={GUIDE_HIGHLIGHT_STROKE_COLOR}
                        strokeWidth={highlightStrokeWidth}
                    />
                ))}
            </svg>

            <AnimatePresence>
                {!transitionActive &&
                    dialogue &&
                    step.dialoguePosition === "corner" && (
                        <motion.div
                            className={styles.cornerDialogue}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                        >
                            <DialogBox
                                leftCharacter={
                                    side === "left"
                                        ? dialogue.speaker
                                        : undefined
                                }
                                leftExpression={
                                    side === "left"
                                        ? dialogue.expression
                                        : undefined
                                }
                                rightCharacter={
                                    side === "right"
                                        ? dialogue.speaker
                                        : undefined
                                }
                                rightExpression={
                                    side === "right"
                                        ? dialogue.expression
                                        : undefined
                                }
                                active={side}
                                text={displayed}
                                fullText={dialogueText}
                            />
                        </motion.div>
                    )}
            </AnimatePresence>

            <button
                className={styles.skipBtn}
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    skipGuide()
                }}
                style={{ pointerEvents: "auto" }}
            >
                {i18n.get("map.guide.skip")}
            </button>
        </div>
    )
}

function PrologueView() {
    const prologueDialogues = useGuideStore((s) => s.prologueDialogues)
    const prologueIndex = useGuideStore((s) => s.prologueIndex)
    const advancePrologue = useGuideStore((s) => s.advancePrologue)

    return (
        <DialoguePhaseView
            dialogues={prologueDialogues}
            index={prologueIndex}
            onAdvance={advancePrologue}
        />
    )
}

function EpilogueView() {
    const epilogueDialogues = useGuideStore((s) => s.epilogueDialogues)
    const epilogueIndex = useGuideStore((s) => s.epilogueIndex)
    const advanceEpilogue = useGuideStore((s) => s.advanceEpilogue)

    return (
        <DialoguePhaseView
            dialogues={epilogueDialogues}
            index={epilogueIndex}
            onAdvance={advanceEpilogue}
        />
    )
}

export const GuideOverlay: React.FC = () => {
    const guidePhase = useGuideStore((s) => s.guidePhase)

    return (
        <AnimatePresence>
            {guidePhase === "prologue" && <PrologueView key="prologue" />}
            {guidePhase === "epilogue" && <EpilogueView key="epilogue" />}
            {guidePhase === "tutorial" && <TutorialView key="tutorial" />}
        </AnimatePresence>
    )
}
